<svelte:options customElement={{ tag: "bt-continue-learning", shadow: "none" }} />

<script lang="ts">
  import ActionButton from '$shared/action-button/ActionButton.svelte';
  import ProgressBar from '$shared/progress-bar/ProgressBar.svelte';
  import Icon from '$shared/icons/Icon.svelte';

  export let dashboardUrl: string = "/dashboard";
  export let item = null;
  export let headerText: string = "Continue your learning";
  export let goToDashboard: string = "Go to your dashboard";
  export let completeText: string = "complete";
  export let resumeText: string = "Resume";
  export let videoText: string = "Video";

  const typeLabelFor = (type: string) => {
  switch (type) {
    case "LearningPath": return "Learning Path";
    case "Course": return "Course";
    case "Curation": return "Curation";
    case "Video": return "Lesson";
    default: return "Lesson";
  }
};

  const normalizeContinueLearningItem = (item: any) => {
    let parsed: any = item;
    if (typeof parsed === "string") {
      try { parsed = JSON.parse(parsed); } catch { parsed = null; }
    }
    if (!parsed || typeof parsed !== "object") {
      return {
        title: "Keep learning",
        author: "",
        meta: "",
        thumbnail_url: "",
        resume_path: dashboardUrl,
        percent_complete: 0,
        duration_label: "",
        content_type: videoText,
        type_label: videoText,
      };
    }
    return {
      title: parsed.title ?? "",
      author: parsed.author ?? "",
      meta: parsed.meta ?? "",
      thumbnail_url: parsed.thumbnail_url || "",
      resume_path: parsed.resume_path || dashboardUrl,
      percent_complete: Number(parsed.percent_complete ?? 0),
      duration_label: parsed.duration_label ?? "",
      content_type: parsed.content_type ?? videoText,
      type_label: parsed.type_label ?? typeLabelFor(String(parsed.content_type ?? videoText))
    };
  };

  
    $: data = normalizeContinueLearningItem(item);
    $: pct = Math.max(0, Math.min(100, Number(data.percent_complete) || 0));
</script>

<section class="continue-learning">
  <div class="continue-learning__header">
    <h2 class="m-0 font-serif text-xl leading-snug">{headerText}</h2>
    <a href={dashboardUrl}>
      {goToDashboard}
      <Icon icon="ArrowRight" iconClass="ml-1 w-5 h-5"></Icon>
    </a>
  </div>
  <div class="continue-learning__card">
    <a href={data.resume_path} class={`continue-learning__card__stack`}>
      <!-- Image mask above the stacks -->
      <div class="absolute inset-0 border border-neutral-350 hover:border-neutral-900 bg-white shadow-[2px_1px_1px_rgba(0,0,0,0.15)]">
        <div class="absolute inset-0 overflow-hidden bg-gray-100">
          <img src={data.thumbnail_url} alt="" class="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
    </a>
    <div class="continue-learning__details">
      <div class="content-card__headline-stack">
        {#if data.type_label}
          <div class="content-card__headline-stack__type">{data.type_label}</div>
        {/if}
        <p class="content-card__headline-stack__headline line-clamp-3">
          <a href={data.resume_path}>
            {data.title}
          </a>
        </p>
        <div class="content-card__expert">
          {data.author}
          <span class="whitespace-normal">{data.meta}</span>
        </div>
        <div class="content-card__duration">
        {#if data.duration_label}
          <Icon icon="Clock" size="small"></Icon><span>{data.duration_label}</span>
        {/if}
      </div>
      </div>

      <div class="continue-learning__progress">
        <ProgressBar color="yellow" numerator={pct} denominator={100}></ProgressBar>
        <div class="progress-meta">
          <span>{pct}% {completeText}</span>
        </div>
      </div>
    </div>
    <div class="continue-learning__button">
      <ActionButton design="primary yellow" link={data.resume_path}>
        <bt-icon icon="Play" size="medium" iconClass="inline-block mr-2"></bt-icon>{resumeText}
      </ActionButton>
    </div>
  </div>
</section>

<style>
  .continue-learning {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: theme('spacing.6');
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    background: white;
    min-height: 358px;
  }

  .continue-learning__header {
    display: flex;
    height: theme('spacing.10');
    width: 100%;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    margin-bottom: theme('spacing.6');
  }

  .continue-learning__header a {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: theme('fontSize.sm');
    line-height: theme('lineHeight.normal');
    white-space: nowrap;
    &:hover {
      text-decoration: underline;
    }
  }

  .continue-learning__card {
    display: flex;
    width: 100%;
    padding: theme('spacing.6');
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    background: white;
    box-shadow: theme('btpShadow.main-shadow');
    gap: theme('spacing.6');
  }

  .continue-learning__card__stack {
    position: relative;
    display: block;
    flex-shrink: 0;
    z-index: 0;
    width: 336px;
    height: 189px;
  }

  .continue-learning__details {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: flex-start;
    flex-grow: 1;
    gap: theme('spacing.6');
    > * {
      max-width: 600px;
    }
  }

  .continue-learning__button {
    display: flex;
    align-items: end;
  }

  .continue-learning__progress {
    align-self: stretch;
  }

  .continue-learning__progress .progress-meta {
  display:flex;
  align-items:center;
}

  @media (max-width: 480px) {
    .continue-learning__progress .progress-meta {
      flex-direction: column;
      align-items: flex-start;
    }
  }


  @media (max-width: 56.25rem) {
    .continue-learning__card {
      flex-direction: column;
      align-items: flex-start;
      > a {
        width: 100%;
        height: auto;
      }
    }
    .continue-learning__card__stack {
      padding-bottom: calc(9 / 16 * 100%);
    }
    .continue-learning__details {
      align-self: initial;
    }
    .continue-learning__header {
      flex-wrap: wrap;
      height: fit-content;
      padding-bottom: theme('spacing.2');
      h2 {
        padding-right: theme('spacing.2');
      }
    }
  }
</style>